"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  FaCheck,
  FaClock,
  FaTimes,
  FaVideo,
  FaTasks
} from "react-icons/fa"
import { Calendar, MapPin, Users, ArrowRight } from "lucide-react"

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`

const formatDateTime = (dateStr) => {
  return new Date(dateStr).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  })
}

const isPast = (dateStr, duree) => {
  const end = new Date(new Date(dateStr).getTime() + duree * 60000)
  return new Date() > end
}

const isNowMeeting = (dateStr, duree) => {
  const now = new Date()
  const start = new Date(dateStr)
  const end = new Date(start.getTime() + duree * 60000)
  return now >= start && now <= end
}

/* ================= EVENT CARD ================= */
const EventCard = ({ event, onJoin, onRespond }) => {
  const [hover, setHover] = useState(false)
  const live = isNowMeeting(event.date_heure, event.duree)
  const past = isPast(event.date_heure, event.duree)

  const getStatusStyle = () => {
    if (live) return { borderLeft: "4px solid #22c55e", background: "#f0fdf4" }
    if (past) return { borderLeft: "4px solid #9ca3af", background: "#f9fafb", opacity: 0.8 }
    return { borderLeft: "4px solid #5e17eb" }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepte':
        return <span style={styles.badgeAccepte}>Accepté</span>
      case 'refuse':
        return <span style={styles.badgeRefuse}>Refusé</span>
      case 'en_attente':
        return <span style={styles.badgeAttente}>En attente</span>
      default:
        return null
    }
  }

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...styles.card,
        ...getStatusStyle(),
        transform: hover ? "translateY(-4px) scale(1.02)" : "translateY(0)",
        boxShadow: hover
          ? "0 8px 20px rgba(94,23,235,0.2)"
          : "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      <div style={styles.cardHeader}>
        <Calendar size={32} color="#5e17eb" />
      </div>

      <div style={styles.cardBody}>
        <div style={styles.cardHeaderRow}>
          <h3 style={styles.cardTitle}>{event.titre}</h3>
          {getStatusBadge(event.statut_participation)}
        </div>

        <div style={styles.cardDate}>
          {formatDateTime(event.date_heure)} ({event.duree} min)
        </div>

        <div style={styles.cardInfo}>
          <div style={styles.infoItem}>
            <MapPin size={12} color="#666" />
            <span>{event.salle}</span>
          </div>
          <div style={styles.infoItem}>
            <Users size={12} color="#666" />
            <span>{event.participants_details?.length || 0} participants</span>
          </div>
        </div>

        {!past && event.statut_participation === 'en_attente' && (
          <div style={styles.invitationActions}>
            <button
              onClick={() => onRespond(event.id, 'accepte')}
              style={styles.acceptButton}
            >
              <FaCheck size={12} /> Accepter
            </button>
            <button
              onClick={() => onRespond(event.id, 'refuse')}
              style={styles.refuseButton}
            >
              <FaTimes size={12} /> Refuser
            </button>
          </div>
        )}

        {event.statut_participation === 'accepte' && (
          <button
            disabled={!live}
            onClick={onJoin}
            style={{
              ...styles.joinButton,
              backgroundColor: live ? "#16a34a" : "#e5e7eb",
              color: live ? "white" : "#9ca3af",
              cursor: live ? "pointer" : "not-allowed",
            }}
          >
            <FaVideo size={14} />
            {live ? "Participer" : past ? "Terminée" : "À venir"}
          </button>
        )}
      </div>
    </div>
  )
}

/* ================= TASK CARD ================= */
const TaskCard = ({ task, onMarkDone }) => {
  const [hover, setHover] = useState(false)
  const late = new Date(task.deadline) < new Date()

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...styles.taskCard,
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        boxShadow: hover
          ? "0 4px 12px rgba(94,23,235,0.15)"
          : "0 1px 4px rgba(0,0,0,0.05)",
      }}
    >
      <div style={styles.taskContent}>
        <div style={styles.taskHeader}>
          <h4 style={styles.taskTitle}>{task.description}</h4>
          {late && <span style={styles.lateBadge}>Retard</span>}
        </div>

        <div style={styles.taskMeta}>
          <span style={styles.taskDeadline}>
            <FaClock size={12} color="#666" /> {new Date(task.deadline).toLocaleDateString('fr-FR')}
          </span>
          <span style={styles.taskReunion}>
            {task.reunion_titre}
          </span>
        </div>

        <button
          onClick={() => onMarkDone(task.id)}
          style={styles.taskButton}
        >
          <FaCheck size={12} /> Marquer comme faite
        </button>
      </div>
    </div>
  )
}

/* ================= MAIN COMPONENT ================= */
export default function MeetPost() {
  const navigate = useNavigate()
  const [meetings, setMeetings] = useState([])
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const getToken = () => localStorage.getItem("access_token")
  const getUserData = () => JSON.parse(localStorage.getItem("auth_data") || "{}")

  const fetchMeetings = async () => {
    try {
      const token = getToken()
      if (!token) {
        setError("Vous devez être connecté")
        setLoading(false)
        return
      }

      const response = await fetch(`${API_URL}/reunions/`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      })

      if (!response.ok) throw new Error("Erreur de chargement")

      const data = await response.json()

      let meetingsData = []
      if (data.results) meetingsData = data.results
      else if (Array.isArray(data)) meetingsData = data
      else if (data.data) meetingsData = data.data

      setMeetings(meetingsData)
    } catch (err) {
      console.error("Erreur:", err)
      setError(err.message)
    }
  }

  const fetchTasks = async () => {
    try {
      const token = getToken()
      const userData = getUserData()

      const response = await fetch(`${API_URL}/taches/`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      })

      if (response.ok) {
        const data = await response.json()
        let tasksData = []
        if (data.results) tasksData = data.results
        else if (Array.isArray(data)) tasksData = data

        const userTasks = tasksData.filter(task =>
          (task.responsable === userData.userId || task.responsable_nom === userData.name) &&
          task.statut !== 'terminee'
        )

        setTasks(userTasks)
      }
    } catch (err) {
      console.error("Erreur chargement tâches:", err)
    }
  }

  useEffect(() => {
    Promise.all([fetchMeetings(), fetchTasks()]).finally(() => setLoading(false))

    const handleInvitationUpdate = () => {
      fetchMeetings()
    }

    window.addEventListener('invitation-updated', handleInvitationUpdate)
    return () => window.removeEventListener('invitation-updated', handleInvitationUpdate)
  }, [])

  const handleInvitationResponse = async (meetingId, reponse) => {
    try {
      const token = getToken()
      const meeting = meetings.find(m => m.id === meetingId)
      if (!meeting) return

      const userData = getUserData()
      const participation = meeting.participations?.find(
        p => p.utilisateur === userData.userId
      )

      if (!participation) return

      const response = await fetch(`${API_URL}/participations/${participation.id}/repondre/`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ statut: reponse })
      })

      if (response.ok) {
        setMeetings(prev =>
          prev.map(m => {
            if (m.id === meetingId) {
              return { ...m, statut_participation: reponse }
            }
            return m
          })
        )

        window.dispatchEvent(new CustomEvent('invitation-updated'))
      }
    } catch (err) {
      console.error("Erreur réponse invitation:", err)
    }
  }

  const markTaskDone = async (taskId) => {
    try {
      const token = getToken()
      const response = await fetch(`${API_URL}/taches/${taskId}/changer_statut/`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ statut: "terminee" })
      })

      if (response.ok) {
        setTasks(prev => prev.filter(t => t.id !== taskId))
      }
    } catch (err) {
      console.error("Erreur:", err)
    }
  }

  const upcomingMeetings = meetings
    .filter(m => {
      const end = new Date(new Date(m.date_heure).getTime() + m.duree * 60000)
      return end > new Date()
    })
    .sort((a, b) => new Date(a.date_heure) - new Date(b.date_heure))
    .slice(0, 3)

  const pendingTasks = tasks.slice(0, 3)
  const invitationsEnAttente = meetings.filter(m => m.statut_participation === 'en_attente').length

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Chargement de votre dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.errorContainer}>
          <p style={{ color: "#ef4444" }}>❌ {error}</p>
          <button onClick={() => window.location.reload()} style={styles.retryButton}>
            Réessayer
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>DASHBOARD EMPLOYÉ</h1>
      <p style={styles.subtitle}>
        Gérez vos réunions et tâches
      </p>

      <div style={styles.statsBar}>
        <div style={styles.statItem}>
          <span style={styles.statValue}>{upcomingMeetings.length}</span>
          <span style={styles.statLabel}>Réunions à venir</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statValue}>{pendingTasks.length}</span>
          <span style={styles.statLabel}>Tâches en cours</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue, color:"#1f1f2e" }}>
            {invitationsEnAttente}
          </span>
          <span style={styles.statLabel}>Invitations en attente</span>
        </div>
      </div>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Réunions à venir</h2>
          <button
            onClick={() => navigate('/mes-reunions')}
            style={styles.viewAllButton}
          >
            Voir toutes <ArrowRight size={14} />
          </button>
        </div>

        {upcomingMeetings.length === 0 ? (
          <div style={styles.emptyState}>
            <Calendar size={32} color="#9ca3af" />
            <p>Aucune réunion à venir</p>
          </div>
        ) : (
          <div style={styles.smallGrid}>
            {upcomingMeetings.map((meeting) => (
              <EventCard
                key={meeting.id}
                event={meeting}
                onJoin={() => navigate(`/consulter-reunion/${meeting.id}`)}
                onRespond={handleInvitationResponse}
              />
            ))}
          </div>
        )}
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Tâches en cours</h2>
          <button
            onClick={() => navigate('/mes-taches')}
            style={styles.viewAllButton}
          >
            Voir toutes <ArrowRight size={14} />
          </button>
        </div>

        {pendingTasks.length === 0 ? (
          <div style={styles.emptyState}>
            <FaTasks size={32} color="#9ca3af" />
            <p>Aucune tâche en cours</p>
          </div>
        ) : (
          <div style={styles.smallGrid}>
            {pendingTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onMarkDone={markTaskDone}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

const styles = {
  page: {
    padding: "22px",
    background: "#f7f6ff",
    minHeight: "100vh",
    borderRadius: "16px"
  },
  title: {
    fontSize: "26px",
    color: "#1f1f2e",
    marginBottom: "4px",
    fontWeight: "500",
  },
  subtitle: {
    color: "#666",
    marginBottom: "18px",
    fontSize: "14px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    gap: "16px"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #5e17eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  errorContainer: {
    textAlign: "center",
    padding: "40px",
    background: "white",
    borderRadius: "12px"
  },
  retryButton: {
    marginTop: "12px",
    padding: "8px 16px",
    background: "#5e17eb",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  statsBar: {
    display: "flex",
    gap: "24px",
    background: "white",
    padding: "16px 24px",
    borderRadius: "12px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    flexWrap: "wrap"
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: "100px"
  },
  statValue: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#1f1f2e"
  },
  statLabel: {
    fontSize: "11px",
    color: "#666",
    marginTop: "4px",
    textAlign: "center"
  },
  badgeAccepte: {
    fontSize: "10px",
    padding: "3px 8px",
    background: "#dcfce7",
    color: "#16a34a",
    borderRadius: "12px",
    fontWeight: "500",
    whiteSpace: "nowrap"
  },
  badgeRefuse: {
    fontSize: "10px",
    padding: "3px 8px",
    background: "#fee2e2",
    color: "#ef4444",
    borderRadius: "12px",
    fontWeight: "500",
    whiteSpace: "nowrap"
  },
  badgeAttente: {
    fontSize: "10px",
    padding: "3px 8px",
    background: "#fef3c7",
    color: "#eab308",
    borderRadius: "12px",
    fontWeight: "500",
    whiteSpace: "nowrap"
  },
  section: {
    marginBottom: "32px"
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px"
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1f1f2e"
  },
  viewAllButton: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 10px",
    background: "transparent",
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    color: "#5e17eb",
    fontSize: "12px",
    cursor: "pointer",
    transition: "all 0.2s"
  },
  emptyState: {
    textAlign: "center",
    padding: "30px",
    background: "white",
    borderRadius: "10px",
    color: "#6b7280",
    fontSize: "13px"
  },
  smallGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "16px"
  },
  card: {
    background: "white",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: {
    height: "90px",
    background: "#f3f0ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  cardBody: {
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    flex: 1
  },
  cardHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "4px",
    gap: "6px"
  },
  cardTitle: {
    fontWeight: "600",
    fontSize: "14px",
    color: "#1f1f2e",
    margin: 0
  },
  cardDate: {
    fontSize: "11px",
    color: "#888",
    marginBottom: "6px"
  },
  cardInfo: {
    display: "flex",
    gap: "10px",
    marginBottom: "8px"
  },
  infoItem: {
    display: "flex",
    alignItems: "center",
    gap: "3px",
    fontSize: "10px",
    color: "#666"
  },
  invitationActions: {
    display: "flex",
    gap: "6px",
    marginTop: "6px"
  },
  acceptButton: {
    flex: 1,
    backgroundColor: "#22c55e",
    color: "white",
    border: "none",
    borderRadius: "5px",
    padding: "5px",
    fontSize: "10px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "3px",
    cursor: "pointer"
  },
  refuseButton: {
    flex: 1,
    backgroundColor: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "5px",
    padding: "5px",
    fontSize: "10px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "3px",
    cursor: "pointer"
  },
  joinButton: {
    marginTop: "6px",
    padding: "5px 0",
    borderRadius: "5px",
    fontWeight: "500",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    border: "none",
    width: "100%",
    fontSize: "11px"
  },
  taskCard: {
    background: "white",
    borderRadius: "8px",
    padding: "12px",
    transition: "all 0.2s ease",
    border: "1px solid #e5e7eb"
  },
  taskContent: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  taskHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "6px"
  },
  taskTitle: {
    fontSize: "13px",
    fontWeight: "500",
    color: "#1f1f2e",
    margin: 0
  },
  lateBadge: {
    fontSize: "9px",
    padding: "2px 5px",
    background: "#fee2e2",
    color: "#ef4444",
    borderRadius: "10px",
    whiteSpace: "nowrap"
  },
  taskMeta: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "10px",
    color: "#666"
  },
  taskDeadline: {
    display: "flex",
    alignItems: "center",
    gap: "3px"
  },
  taskReunion: {
    color: "#5e17eb",
    fontSize: "10px"
  },
  taskButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    padding: "5px",
    background: "#e6f7e6",
    border: "none",
    borderRadius: "5px",
    color: "#16a34a",
    fontSize: "11px",
    cursor: "pointer",
    transition: "background 0.2s"
  }
}

const styleSheet = document.createElement("style")
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`
document.head.appendChild(styleSheet)